/**
 * Application Designer — what a native page declares about itself.
 *
 * A native page stays the application: it fetches data, enforces permissions,
 * runs clinical rules and owns routing. The only thing it hands the designer is
 * a descriptor — a stable page id, a layout mode, and a manifest of the
 * elements it is willing to have rearranged.
 *
 * The manifest is the AUTHORITY, not the saved layout. `layoutDocument` rebuilds
 * every document from the descriptor and merges saved values in, so an element
 * the native page stops shipping disappears from saved layouts without a
 * migration, and one it starts shipping appears at its declared default. That is
 * the property that lets the Application Designer be safe across releases, and
 * it is the rule `patientSummaryLayout` already followed for cards.
 *
 * Two things a descriptor deliberately does NOT contain:
 *
 *   - A component. An element carries an id; native code decides what that id
 *     renders. `renderSummaryCard(id, props)` is exactly this indirection, and
 *     it is why a saved layout cannot name something the page did not compile
 *     in. `componentRegistry` is the Phase 3 hook for ADDING approved elements
 *     and is empty in Phase 1; even then it will hold ids, never components.
 *   - A permission decision. `accessKey` names a gate the HOST evaluates live on
 *     every render. Nothing about access is ever persisted.
 */

import { isOptInCosmetic } from "./cosmetics";
import { LAYOUT_MODE_GRID, normalizeLayoutMode } from "./layoutModes";

/**
 * What the designer may do to one element. A page turns a capability off when
 * its native implementation cannot honour it, so the properties panel offers a
 * control only where the control actually does something.
 */
const DEFAULT_CAPABILITIES = Object.freeze({
  hide: true,
  move: true,
  resize: true,
  style: true,
});

function normalizeCosmetics(raw) {
  if (!Array.isArray(raw)) return Object.freeze([]);
  const kept = [];
  raw.forEach((key) => {
    if (typeof key !== "string" || !isOptInCosmetic(key) || kept.includes(key)) return;
    kept.push(key);
  });
  return Object.freeze(kept);
}

function normalizeCapabilities(raw, mode) {
  const merged = { ...DEFAULT_CAPABILITIES, ...(raw || {}) };
  return Object.freeze({
    hide: merged.hide !== false,
    move: merged.move !== false,
    /** Flow never resizes: a content-sized element owns its own height. */
    resize: mode === LAYOUT_MODE_GRID && merged.resize !== false,
    style: merged.style !== false,
  });
}

function normalizeElement(raw, index, mode) {
  const id = String(raw?.id || "").trim();
  if (!id) return null;
  return Object.freeze({
    id,
    title: String(raw?.title || "").trim() || id,
    column: Number.isInteger(raw?.column) ? raw.column : 0,
    order: Number.isFinite(raw?.order) ? raw.order : index,
    col: Math.max(1, Number.parseInt(raw?.col, 10) || 1),
    row: Math.max(1, Number.parseInt(raw?.row, 10) || 1),
    width: Math.max(1, Number.parseInt(raw?.width, 10) || 1),
    height: Math.max(1, Number.parseInt(raw?.height, 10) || 1),
    /** Host-evaluated gate. A falsy value means the element is always allowed. */
    accessKey: raw?.accessKey ? String(raw.accessKey) : null,
    capabilities: normalizeCapabilities(raw?.capabilities, mode),
    /** Opt-in native-card cosmetics. Empty means the Card group stays hidden. */
    cosmetics: normalizeCosmetics(raw?.cosmetics),
  });
}

export function definePageDescriptor(spec) {
  const page = String(spec?.page || "").trim();
  if (!page) throw new Error("A page descriptor needs a stable page id.");
  const mode = normalizeLayoutMode(spec?.mode);
  const elements = (spec?.elements || [])
    .map((element, index) => normalizeElement(element, index, mode))
    .filter(Boolean);
  const seen = new Set();
  const unique = elements.filter((element) => {
    if (seen.has(element.id)) return false;
    seen.add(element.id);
    return true;
  });
  return Object.freeze({
    page,
    title: String(spec?.title || "").trim() || page,
    mode,
    columns: Math.max(1, Number.parseInt(spec?.columns, 10) || 1),
    rows: Math.max(1, Number.parseInt(spec?.rows, 10) || 1),
    elements: Object.freeze(unique),
    elementIds: Object.freeze(unique.map((element) => element.id)),
    /** Phase 3: approved element ids a designer may ADD. Empty in Phase 1. */
    componentRegistry: Object.freeze([...(spec?.componentRegistry || [])]),
  });
}

const REGISTRY = new Map();

export function registerPageDescriptor(descriptor) {
  REGISTRY.set(descriptor.page, descriptor);
  return descriptor;
}

export function findPageDescriptor(page) {
  return REGISTRY.get(String(page || "")) || null;
}

export function designablePages() {
  return [...REGISTRY.values()];
}

export function descriptorElement(descriptor, id) {
  return (descriptor?.elements || []).find((element) => element.id === id) || null;
}

export function isDescriptorElement(descriptor, id) {
  return Boolean(descriptorElement(descriptor, id));
}

export function descriptorElementTitle(descriptor, id) {
  return descriptorElement(descriptor, id)?.title || id;
}

export function descriptorCapabilities(descriptor, id) {
  return descriptorElement(descriptor, id)?.capabilities || DEFAULT_CAPABILITIES;
}

export function descriptorCosmetics(descriptor, id) {
  return descriptorElement(descriptor, id)?.cosmetics || Object.freeze([]);
}

/**
 * Whether the properties panel should offer this cosmetic for the element.
 * Universal cosmetics follow `capabilities.style`. Opt-in box cosmetics follow
 * the element's declared list, so Vital Signs can grow Card Width without
 * every other card growing the same controls.
 */
export function elementAcceptsCosmetic(descriptor, id, key) {
  if (!isOptInCosmetic(key)) return descriptorCapabilities(descriptor, id).style;
  return descriptorCosmetics(descriptor, id).includes(key);
}

/**
 * Whether an element may render for this viewer. Evaluated live against the
 * host's access map, never read from or written to a saved layout.
 *
 * While designing, an element the viewer cannot normally see is still shown, so
 * an administrator arranging the page sees the whole page. That is only safe
 * because the native page never hands over data it has not permission-checked —
 * the element renders empty rather than leaking. A host that fetches data it has
 * not gated must not rely on this.
 */
export function elementAllowedForAccess(descriptor, id, access = {}, { designing = false } = {}) {
  if (designing) return true;
  const gate = descriptorElement(descriptor, id)?.accessKey;
  if (!gate) return true;
  return Boolean(access[gate]);
}
